import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { C_ON_SURFACE, C_WHITE } from "~/common/components/icon/Colors";
import { useEffect, useRef } from "react";
import type { Chart } from "chart.js";
import { FONT_FAMILY } from "~/common/components/materialInput/Fonts";

export function centeredTextPlugin(text: string, fontSize: string, color: string) {
  return {
    id: "centeredTextPlugin",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    beforeDraw: (chart: any) => {
      const ctx = chart.ctx;
      const xCoor = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
      const yCoor = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;
      const maxWidth = chart.chartArea.right - chart.chartArea.left;
      ctx.save();
      ctx.font = `${fontSize} ${FONT_FAMILY}`;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, xCoor, yCoor, maxWidth);
      ctx.restore();
    },
  };
}

export function rltPlugin(monthAmount: number, label: string) {
  return {
    id: "RLT",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    afterDraw: (chart: any) => {
      const topPadding = chart.config.options.layout.padding.top;

      const arrowStart = chart.scales.x.getPixelForValue(chart.data.labels[0]);
      let arrowFinish = undefined;
      if (monthAmount <= 1) {
        const nextMonth = chart.scales.x.getPixelForValue(chart.data.labels[1]);
        if (nextMonth) {
          arrowFinish = nextMonth - (nextMonth - arrowStart) / 2;
        }
      } else {
        const monthFinish = Math.min(chart.data.labels.length - 1, monthAmount - 1);
        arrowFinish = chart.scales.x.getPixelForValue(chart.data.labels[monthFinish]);
      }

      if (arrowFinish === undefined) {
        return;
      }

      const arrowHeight = topPadding / 2;

      drawRltArrow(chart.ctx, arrowStart, arrowHeight, arrowFinish, arrowHeight, C_ON_SURFACE);
      drawRltText(chart.ctx, label, arrowStart, arrowFinish, arrowHeight, C_ON_SURFACE);
    },
  };
}

function drawRltText(
  context: CanvasRenderingContext2D,
  text: string,
  fromx: number,
  tox: number,
  y: number,
  fillStyle: string,
) {
  context.save();

  const textWidth = context.measureText(text).width;

  // Set the font properties
  context.font = `13px ${FONT_FAMILY}`;
  const textHeight = parseInt(context.font);
  const x = fromx + (tox - fromx) / 2 - textWidth / 2;
  // white background behind the text
  context.fillStyle = C_WHITE;
  const xPadding = 8;
  context.fillRect(x - xPadding, y - textHeight / 2, textWidth + 2 * xPadding, textHeight);
  context.fillStyle = fillStyle || C_WHITE;
  context.fillText(text, x, y);

  context.restore();
}

function drawRltArrow(
  context: CanvasRenderingContext2D,
  fromx: number,
  fromy: number,
  tox: number,
  toy: number,
  strokeStyle: string,
) {
  const DASH_LENGTH = 9;
  const HEAD_LEN = 6; // length of head in pixels
  const angle = Math.atan2(toy - fromy, tox - fromx);
  context.save();
  context.strokeStyle = strokeStyle || "#000"; // defaults to black
  // dashed part
  context.beginPath();
  context.setLineDash([DASH_LENGTH]);
  context.moveTo(fromx, fromy);
  context.lineTo(tox, toy);
  context.stroke();
  // second part -non dashed-
  // Head at right end
  context.beginPath();
  context.setLineDash([0]);
  context.moveTo(tox, toy);
  context.lineTo(tox - HEAD_LEN * Math.cos(angle - Math.PI / 6), toy - HEAD_LEN * Math.sin(angle - Math.PI / 6));
  context.moveTo(tox, toy);
  context.lineTo(tox - HEAD_LEN * Math.cos(angle + Math.PI / 6), toy - HEAD_LEN * Math.sin(angle + Math.PI / 6));
  context.stroke();
  // Head at left end
  context.beginPath();
  context.moveTo(fromx, fromy);
  context.lineTo(fromx + HEAD_LEN * Math.cos(angle - Math.PI / 6), fromy + HEAD_LEN * Math.sin(angle - Math.PI / 6));
  context.moveTo(fromx, fromy);
  context.lineTo(fromx + HEAD_LEN * Math.cos(angle + Math.PI / 6), fromy + HEAD_LEN * Math.sin(angle + Math.PI / 6));
  context.stroke();
  //
  context.restore(); // this will, in fact, restore strokeStyle
}

export function formatYTicks(value: number | string) {
  value = Number(value);
  if (value % 1 !== 0) {
    return " ";
  }
  let suffix = "";
  if (value >= 1_000_000) {
    value = value / 1_000_000;
    suffix = " m";
  } else if (value >= 1_000) {
    value = value / 1_000;
    suffix = " k";
  }
  return NF.getDefaultFormatter().format(value) + suffix;
}

export const useRerenderOnResize = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Chart | undefined>(undefined);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [containerRef.current, chartRef.current]);

  return [containerRef, chartRef] as const;
};
